/**
 * useSignInWithPin — PIN-based sign-in.
 *
 * Two-step flow:
 *   1. POST {email, pin} to the pin-signin Edge Function (unauthenticated).
 *      The server verifies the PIN against the bcrypt-equivalent hash
 *      and on success returns an OTP minted via auth.admin.generateLink.
 *   2. Client calls supabase.auth.verifyOtp({ email, token: otp,
 *      type: 'email' }) to redeem the OTP for a real Supabase session
 *      (access + refresh tokens managed by Supabase Auth, with
 *      refresh-token rotation + reuse detection).
 *
 * The two-step is intentional — Supabase Auth owns session issuance
 * (refresh tokens live in auth.refresh_tokens server-side), and we
 * hook into it via the documented OTP-redemption path rather than
 * hand-signing JWTs.
 *
 * Returned errors are structured AppError instances. The 423 PIN_LOCKED
 * branch carries `retryAfter` in the AppError so the LoginPage can
 * surface a precise countdown.
 */

import { useState }   from 'react'
import { supabase }   from '@/lib/supabase'
import { AppError }   from '@/lib/errors/AppError'

export interface PinSigninResult {
  signInWithPin: (email: string, pin: string) => Promise<void>
  isPending:     boolean
  error:         AppError | null
}

export function useSignInWithPin(): PinSigninResult {
  const [isPending, setPending] = useState(false)
  const [error,     setError]   = useState<AppError | null>(null)

  const signInWithPin = async (rawEmail: string, pin: string): Promise<void> => {
    const email = rawEmail.trim().toLowerCase()
    setPending(true)
    setError(null)

    let invokeData: unknown
    let invokeError: { message: string; status?: number; context?: Response } | null = null
    try {
      const result = await supabase.functions.invoke('pin-signin', {
        body: { email, pin },
      })
      invokeData  = result.data
      invokeError = result.error as typeof invokeError
    } catch (e) {
      invokeError = { message: e instanceof Error ? e.message : 'Network error' }
    }

    if (invokeError) {
      const status   = invokeError.status ?? 500
      // The functions client returns the response body in `context`
      // when the function returned a non-2xx status. We surface the
      // server's error code + detail so the UI can render specific
      // messages (PIN_LOCKED with retry-after, INVALID_CREDENTIALS,
      // RATE_LIMITED, etc.).
      let serverCode    = 'PIN_SIGNIN_FAILED'
      let serverDetail  = invokeError.message
      let retryAfter:   number | null = null
      if (invokeError.context) {
        try {
          const body = await invokeError.context.clone().json() as { error?: string; detail?: string }
          if (typeof body.error === 'string')   serverCode   = body.error
          if (typeof body.detail === 'string')  serverDetail = body.detail
          const ra = invokeError.context.headers.get('Retry-After')
          if (ra) {
            const n = parseInt(ra, 10)
            if (Number.isFinite(n)) retryAfter = n
          }
        } catch {
          // body wasn't JSON; keep defaults
        }
      }
      const appErr = new AppError({
        code:        serverCode,
        message:     serverDetail,
        userMessage: friendlyMessage(serverCode, serverDetail, retryAfter),
        status,
      })
      setPending(false)
      setError(appErr)
      throw appErr
    }

    const data = invokeData as { email?: unknown; otp?: unknown; expires_in?: unknown } | null
    if (!data || typeof data.email !== 'string' || typeof data.otp !== 'string') {
      const appErr = new AppError({
        code:        'PIN_SIGNIN_BAD_RESPONSE',
        message:     'pin-signin returned an unexpected response shape',
        userMessage: 'Sign-in failed. Please try again.',
        status:      500,
      })
      setPending(false)
      setError(appErr)
      throw appErr
    }

    // Step 2 — exchange the OTP for a real Supabase session.
    const { error: otpError } = await supabase.auth.verifyOtp({
      email: data.email,
      token: data.otp,
      type:  'email',
    })

    setPending(false)

    if (otpError) {
      const appErr = new AppError({
        code:        'OTP_REDEEM_FAILED',
        message:     otpError.message,
        userMessage: 'Sign-in failed at the session-issuance step. Please try again.',
        status:      500,
        cause:       otpError,
      })
      setError(appErr)
      throw appErr
    }
  }

  return { signInWithPin, isPending, error }
}

function friendlyMessage(code: string, detail: string, retryAfter: number | null): string {
  switch (code) {
    case 'INVALID_CREDENTIALS':
      return 'Email or PIN was incorrect. If you forgot your PIN, use the "Forgot PIN" link.'
    case 'PIN_LOCKED':
      if (retryAfter && retryAfter > 0) {
        const minutes = Math.max(1, Math.ceil(retryAfter / 60))
        return `Too many wrong PIN attempts. Try again in ${minutes} minute${minutes === 1 ? '' : 's'}.`
      }
      return detail || 'Too many wrong PIN attempts. Try again later.'
    case 'RATE_LIMITED':
      return 'Too many sign-in attempts. Please wait a moment and try again.'
    case 'OTP_MINT_FAILED':
    case 'AUDIT_FAILED':
      return 'Sign-in failed at the session-issuance step. Please try again.'
    default:
      return detail || 'Sign-in failed. Please try again.'
  }
}
